/**
 * Product Carousel Manager - Moon Moon Agency
 * Carousel produits "Vous aimerez aussi" / "Consulté récemment" :
 *  - <mm-product-feed> : lazy-load des cards dans le .splide__list du shell, puis mount Splide
 *  - toggle accessible entre les deux onglets (chaque panneau a son propre shell .splide)
 *  - enregistrement des produits consultés (localStorage)
 */

/*********************************************************************************************
 * 👁️ RECENTLY VIEWED — store localStorage
 */
const RecentlyViewedStore = {
	key: 'mm:recently-viewed',
	max: 20,

	read() {
		try {
			const raw = localStorage.getItem(this.key);
			return raw ? JSON.parse(raw) : [];
		} catch (error) {
			return [];
		}
	},

	/**
	 * Ajoute un handle en tête de liste (dédoublonné, capé à `max`).
	 */
	push(handle) {
		if (!handle) return;
		let list = this.read().filter((h) => h !== handle);
		list.unshift(handle);
		list = list.slice(0, this.max);
		try {
			localStorage.setItem(this.key, JSON.stringify(list));
		} catch (error) {
			/* quota / mode privé : ignoré */
		}
	},
};

/*********************************************************************************************
 * 🧩 MM-PRODUCT-FEED — custom element, lazy-load des cards dans le shell
 */
class MMProductFeed extends HTMLElement {
	connectedCallback() {
		this.mode = this.dataset.mode || 'reco';
		this.url = this.dataset.url;
		this.limit = parseInt(this.dataset.limit, 10) || 4;
		this.currentHandle = this.dataset.currentHandle || null;
		if (!this.url) return;

		new IntersectionObserver(this._onIntersect.bind(this), {
			rootMargin: '0px 0px 400px 0px',
		}).observe(this);
	}

	_onIntersect(entries, observer) {
		if (!entries[0].isIntersecting) return;
		observer.unobserve(this);
		this._load();
	}

	_load() {
		let url = this.url;
		let handles = null;

		if (this.mode === 'recently') {
			handles = RecentlyViewedStore.read()
				.filter((h) => h && h !== this.currentHandle)
				.slice(0, this.limit);
			if (!handles.length) {
				this._empty();
				return;
			}
			url += encodeURIComponent(handles.map((h) => `handle:${h}`).join(' OR '));
		}

		fetch(url)
			.then((response) => response.text())
			.then((text) => {
				const wrapper = document.createElement('div');
				wrapper.innerHTML = text;
				const feed = wrapper.querySelector('product-recommendations');
				const slides = feed ? feed.innerHTML.trim() : '';

				if (slides.length) {
					this._inject(slides, handles);
					window.emit('global:product-feed-loaded', { mode: this.mode, feed: this });
				} else {
					this._empty();
				}
			})
			.catch((error) => {
				window.log('error', 'MMProductFeed', 'Feed load failed', { mode: this.mode, error: error.message });
				this._empty();
			});
	}

	/**
	 * Injecte les <li> dans le .splide__list du shell, retire le skeleton, monte Splide.
	 */
	_inject(slidesHTML, handles) {
		const list = this.querySelector('[data-carousel-list]');
		if (!list) return;
		list.innerHTML = slidesHTML;
		list.removeAttribute('data-carousel-list');
		if (handles) this._reorderByHandle(list, handles);
		if (window.wishlistManager) window.wishlistManager.initButtons();
		// Re-attache le handler AJAX d'ajout panier aux forms injectes (sinon submit natif -> /cart en 404)
		if (typeof window.initAddToCartForms === 'function') window.initAddToCartForms();

		// La classe `splide` est ajoutee SEULEMENT maintenant : SplideManager ignore le
		// skeleton au chargement et monte une seule fois, avec les vraies cards en place
		// (sinon double-init + arrows masquees a tort sur un track encore vide).
		const shell = this.querySelector('[data-splide-shell]');
		if (shell) shell.classList.add('splide');
		if (window.SplideManager) window.SplideManager.init();
		if (shell) this._refreshWhenReady(shell);
	}

	/**
	 * Au mount, les images des cards ne sont pas encore chargees : Splide calcule
	 * `isOverflow` sur des largeurs incompletes et n'applique pas `is-overflow`
	 * (fleches masquees a tort a la 1ere arrivee). On refresh une fois les images
	 * chargees pour recalculer le layout et poser `is-overflow`.
	 */
	_refreshWhenReady(shell) {
		const refresh = () => shell._splide && shell._splide.refresh();
		// Refresh apres le prochain paint (double rAF = layout initial stabilise)
		requestAnimationFrame(() => requestAnimationFrame(refresh));
		// Puis a chaque image chargee (largeurs finales, cache froid = 1ere visite)
		shell.querySelectorAll('.splide__slide img').forEach((img) => {
			if (!img.complete) img.addEventListener('load', refresh, { once: true });
		});
	}

	/**
	 * La Search API n'ordonne pas par pertinence des handles : on réordonne les slides
	 * selon l'ordre de consultation (plus récent en premier).
	 */
	_reorderByHandle(list, handles) {
		Array.from(list.children)
			.map((slide) => {
				const link = slide.querySelector('a[href*="/products/"]');
				const match = link ? link.getAttribute('href').match(/\/products\/([^/?#]+)/) : null;
				return { slide, handle: match ? match[1] : '' };
			})
			.sort((a, b) => handles.indexOf(a.handle) - handles.indexOf(b.handle))
			.forEach(({ slide }) => list.appendChild(slide));
	}

	_empty() {
		const panel = this.closest('.mm-product-carousel__panel');
		if (panel) panel.dataset.empty = 'true';
		window.emit('global:product-feed-empty', { mode: this.mode });
		window.ProductCarouselManager?.refreshTabs();
	}
}

/*********************************************************************************************
 * 🎠 PRODUCT CAROUSEL MANAGER — toggle
 */
const ProductCarouselManager = {
	config: {
		selectors: {
			root: '.mm-product-carousel__inner',
			tab: '.mm-product-carousel__tab',
			panel: '.mm-product-carousel__panel',
		},
		classes: { active: 'is-active', inactive: 'mm-black-40', activeColor: 'mm-black' },
	},

	init() {
		const roots = window.getElements(this.config.selectors.root);
		if (!roots.length) return;

		roots.forEach((root) => this._initRoot(root));
		window.log('info', '🎠 [PRODUCT_CAROUSEL]', 'Initialized');
	},

	_initRoot(root) {
		const panels = root.querySelectorAll(this.config.selectors.panel);
		if (panels.length < 2) return; // un seul panneau : pas de toggle

		// Délégation : chaque shell a ses propres onglets (titres dupliqués)
		root.addEventListener('click', (e) => {
			const tab = e.target.closest(this.config.selectors.tab);
			if (tab) this._activate(root, tab.dataset.tab);
		});
		root.addEventListener('keydown', (e) => {
			const tab = e.target.closest(this.config.selectors.tab);
			if (!tab) return;

			// Enter / Space : activer l'onglet focus
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				this._activate(root, tab.dataset.tab);
				return;
			}

			// Fleches : naviguer entre onglets (pattern WAI-ARIA tabs)
			if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
				e.preventDefault();
				const tabs = Array.from(tab.closest('[role="tablist"]').querySelectorAll(this.config.selectors.tab));
				const dir = e.key === 'ArrowRight' ? 1 : -1;
				const nextTab = tabs[(tabs.indexOf(tab) + dir + tabs.length) % tabs.length];
				if (nextTab && nextTab !== tab) {
					this._activate(root, nextTab.dataset.tab);
					// focus l'onglet equivalent dans le shell desormais visible
					const activeTab = root.querySelector(`${this.config.selectors.panel}:not([hidden]) ${this.config.selectors.tab}[data-tab="${nextTab.dataset.tab}"]`);
					if (activeTab) activeTab.focus();
				}
			}
		});

		// Mobile + mode toggle : afficher l'onglet choisi par le marchand (data-mobile-default-tab)
		if (root.dataset.mobileLayout === 'toggle' && window.isTablet()) {
			const target = root.dataset.mobileDefaultTab;
			const targetPanel = root.querySelector(`${this.config.selectors.panel}[data-panel="${target}"]`);
			if (targetPanel && targetPanel.hidden) this._activate(root, target);
		}
	},

	/**
	 * Active l'onglet `name` : affiche son panneau, masque l'autre, synchronise
	 * l'état actif/ARIA des onglets dans TOUS les shells, refresh Splide au reveal.
	 */
	_activate(root, name) {
		const { active, inactive, activeColor } = this.config.classes;

		root.querySelectorAll(this.config.selectors.tab).forEach((tab) => {
			const isActive = tab.dataset.tab === name;
			tab.classList.toggle(active, isActive);
			tab.classList.toggle(activeColor, isActive);
			tab.classList.toggle(inactive, !isActive);
			tab.setAttribute('aria-selected', String(isActive));
			tab.setAttribute('tabindex', isActive ? '0' : '-1');
		});

		root.querySelectorAll(this.config.selectors.panel).forEach((panel) => {
			panel.hidden = panel.dataset.panel !== name;
		});

		// Splide ne mesure pas un track masqué : refresh au reveal
		const activePanel = root.querySelector(`${this.config.selectors.panel}[data-panel="${name}"]`);
		const splide = activePanel && activePanel.querySelector('.mm-product-carousel__splide');
		if (splide && splide._splide) splide._splide.refresh();
	},

	/**
	 * Réévalue les onglets quand un panneau se vide : masque l'onglet sans contenu
	 * (dans tous les shells), masque la section si tout est vide, rebascule si besoin.
	 */
	refreshTabs() {
		window.getElements(this.config.selectors.root).forEach((root) => {
			const panels = Array.from(root.querySelectorAll(this.config.selectors.panel));
			const livePanels = panels.filter((p) => p.dataset.empty !== 'true');
			const emptyNames = panels.filter((p) => p.dataset.empty === 'true').map((p) => p.dataset.panel);

			emptyNames.forEach((name) => {
				root.querySelectorAll(`${this.config.selectors.tab}[data-tab="${name}"]`).forEach((tab) => {
					tab.hidden = true;
				});
			});

			if (!livePanels.length) {
				const section = root.closest('section');
				if (section) section.style.display = 'none';
				return;
			}

			// Si le panneau visible s'est vidé, basculer vers un panneau vivant
			const visiblePanel = panels.find((p) => !p.hidden);
			if (!visiblePanel || visiblePanel.dataset.empty === 'true') {
				this._activate(root, livePanels[0].dataset.panel);
			}
		});
	},
};

/*********************************************************************************************
 * 🎯 INITIALIZATION
 */
customElements.define('mm-product-feed', MMProductFeed);
window.RecentlyViewedStore = RecentlyViewedStore;
window.ProductCarouselManager = ProductCarouselManager;

const initProductCarousel = () => {
	// Enregistre le produit courant (la section expose son handle sur les pages produit)
	const root = document.querySelector('.mm-product-carousel__inner[data-current-handle]');
	if (root) RecentlyViewedStore.push(root.dataset.currentHandle);

	ProductCarouselManager.init();
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initProductCarousel);
} else {
	initProductCarousel();
}
